import { Button, Center, Loader, Modal, Text, Title } from "@mantine/core";
import { useCallback, useEffect, useState } from "react";
import api from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import urls from "../../../urls";
import type { TaskStatusResponse } from "../../../utils/api-utils";
import { pollTaskStatus } from "../../../utils/api-utils";
import type { Todo } from "../../../utils/backend-types";
import type { SiteName } from "../../../utils/enums";
import { TodoDescription } from "../TodoDescription/TodoDescription";
import type { TodoUserDecision } from "../Todos.types";
import { TodoSystemDecision } from "../Todos.types";
import { TodoSwitch } from "../TodoSwitch/TodoSwitch";

declare const SITE_NAME: SiteName;

interface ClarifyTodoModalProps {
  isOpen: boolean;
  todo: Todo;
  currentUserDecision: TodoUserDecision;
  todoIsFinished: boolean;
  onToggle: () => void;
  onTodoClarified: (
    assessmentCompleted: boolean,
    localUserDecision: TodoUserDecision,
    localFinished: boolean
  ) => void;
}

function ClarifyTodoModal({
  isOpen,
  todo,
  currentUserDecision,
  todoIsFinished,
  onToggle,
  onTodoClarified
}: ClarifyTodoModalProps) {
  const [localUserDecision, setLocalUserDecision] =
    useState(currentUserDecision);
  const [localFinished, setLocalFinished] = useState(todoIsFinished);
  const [assessmentCompleted, setAssessmentCompleted] = useState(false);

  function handleAssessmentCompleted() {
    setAssessmentCompleted(true);
  }

  function handleClickSave() {
    onTodoClarified(assessmentCompleted, localUserDecision, localFinished);
    onToggle();
  }

  return (
    <Modal
      className="ClarifyTodoModal"
      opened={isOpen}
      size="xl"
      title={todo.label}
      onClose={onToggle}
    >
      <Text>
        Um zu bewerten, ob Sie diese Aufgabe erfüllen müssen, sind weitere
        Informationen erforderlich. Beantworten Sie hierzu bitte die noch
        offenen Fragen.
      </Text>
      <Title mb="sm" mt="lg" order={5}>
        Beschreibung
      </Title>
      <TodoDescription
        regulatoryDuty={todo.regulatoryDuty}
        userDefinedDescription={todo.description}
      />
      <Title mb="sm" mt="lg" order={5}>
        Offene Fragen
      </Title>
      <RuleAssessment
        todoId={todo.id}
        onAssessmentCompleted={handleAssessmentCompleted}
      />
      <Title mb="sm" mt="lg" order={5}>
        Status
      </Title>
      <TodoSwitch
        finished={localFinished}
        userDecision={localUserDecision}
        onChangeUserDecision={(decision, finished: boolean) => {
          setLocalUserDecision(decision);
          setLocalFinished(finished);
        }}
      />
      <ModalFooter>
        <Button variant="default" onClick={onToggle}>
          Schließen
        </Button>
        <Button color="secondary" onClick={handleClickSave}>
          Speichern
        </Button>
      </ModalFooter>
    </Modal>
  );
}

interface RuleAssessmentProps {
  todoId: number;
  onAssessmentCompleted: () => void;
}

function RuleAssessment({
  todoId,
  onAssessmentCompleted
}: RuleAssessmentProps) {
  const [loadingUpdatedTodo, setLoadingUpdatedTodo] = useState(false);
  const [updatedTodo, setUpdatedTodo] = useState<Todo | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { showErrorToast } = useToast();

  const triggerAssessment = useCallback(async () => {
    setSubmitting(true);

    function handleDone() {
      setSubmitting(false);
      setLoadingUpdatedTodo(true);
      onAssessmentCompleted();

      api.get<Todo>(urls.api.todosDetail(todoId)).then((response) => {
        setUpdatedTodo(response.data);
        setLoadingUpdatedTodo(false);
      });
    }

    function handleError(error: unknown) {
      showErrorToast(error);
      setSubmitting(false);
    }

    try {
      const response = await api.post<TaskStatusResponse>(
        urls.api.todoRuleAssessment(todoId)
      );
      const taskStatusUrl = response.data.taskStatusUrl;

      if (taskStatusUrl) {
        pollTaskStatus(taskStatusUrl, handleDone, handleError);
      } else {
        handleDone();
      }
    } catch (error) {
      handleError(error);
    }
  }, [onAssessmentCompleted, showErrorToast, todoId]);

  useEffect(() => {
    triggerAssessment();
  }, [triggerAssessment]);

  function getInfoText() {
    const defaultText = (
      <span>
        Um zu bewerten, ob Sie diese Aufgabe erfüllen müssen, sind weitere
        Informationen erforderlich. Beantworten Sie hierzu bitte die noch
        offenen Fragen. Sollten Sie die Fragen aktuell nicht beantworten können,
        können Sie die Aufgabe zunächst mit dem Status &quot;zu klären&quot; in
        Ihre Aufgabenliste übernehmen und fehlende Informationen später
        eintragen. Bei Rückfragen steht Ihnen Ihr Ansprechpartner bei{" "}
        {SITE_NAME} gerne zur Verfügung.
      </span>
    );

    if (!updatedTodo) {
      return defaultText;
    }

    switch (updatedTodo.systemDecision) {
      case TodoSystemDecision.Applicable:
        return (
          <span>
            Auf Basis Ihrer Angaben gehen wir davon aus, dass die betreffende
            Aufgabe <strong>erfüllt werden muss</strong>. Sofern dies noch nicht
            erfolgt ist, empfehlen wir den Status <i>Als offen übernehmen</i>{" "}
            auszuwählen. Die Aufgabe wird dann als offene Aufgaben in Ihre Liste
            übernommen. Sofern die Aufgabe bereits erfüllt wurde, können Sie{" "}
            <i>Als erledigt übernehmen</i> auswählen. Die Aufgabe wird dann als
            bereits erledigt in Ihre Liste übernommen.
          </span>
        );
      case TodoSystemDecision.NotApplicable:
        return (
          <span>
            Auf Basis Ihrer Angaben wurde die Aufgabe als{" "}
            <strong>nicht zu erfüllen</strong> erkannt. Sie können{" "}
            <i>Nicht übernehmen</i> auswählen und speichern. Die Aufgabe wird
            nicht in Ihre Aufgabenliste übernommen.
          </span>
        );
      case TodoSystemDecision.CantTell:
        return (
          <span>
            Auf Basis Ihrer Angabe können wir keine eindeutige Entscheidung
            vorschlagen. Bitte treffen Sie eigenständig eine Wahl oder
            kontaktieren Ihren Ansprechpartner bei {SITE_NAME}.
          </span>
        );
      default:
        return defaultText;
    }
  }

  if (updatedTodo) {
    return <div>{getInfoText()}</div>;
  }

  if (loadingUpdatedTodo) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  if (submitting) {
    return (
      <Center>
        <Loader />
      </Center>
    );
  }

  return null;
}

export { ClarifyTodoModal, ClarifyTodoModalProps };
